// eslint-disable-next-line no-restricted-imports
import { isCustomApiError, safeRequest, useIntl } from '@capsicum/common';
import { useCallback } from 'react';
import { type FieldPath } from 'react-hook-form';
import { match } from 'ts-pattern';

import { postValidationMobileNumber } from '../data-access';
import {
	type MobileNumberAvailability,
	type MobileNumberValidationParams,
	type PhoneNumberFormattedMedium
} from '../models';

import { type SmsLoginFormType } from './useAuthFormHelper';

export const useMobileNumberValidation = () => {
	const { $t } = useIntl();

	return useCallback(
		async (
			validationParams: MobileNumberValidationParams
		): Promise<{
			error?: { field: FieldPath<SmsLoginFormType>; message?: string; type: string };
			formattedValues?: PhoneNumberFormattedMedium;
		}> => {
			const { data, error, success } = await safeRequest(() => postValidationMobileNumber(validationParams));

			if (success) {
				const err = match<
					MobileNumberAvailability,
					{ field: FieldPath<SmsLoginFormType>; message?: string; type: string } | undefined
				>(data)
					.with({ canRegister: false, isAvailable: true }, () => ({
						field: 'country',
						message: $t('auth.blockedCountry'),
						type: 'api'
					}))
					.with({ isAvailable: true, isValid: false }, () => ({
						field: 'mobileNumber',
						message: $t('auth.invalidMobileNumber'),
						type: 'api'
					}))
					.otherwise(() => undefined);

				return {
					error: err,
					formattedValues: {
						canRegister: data.canRegister,
						countryCode: validationParams.countryCode,
						email: data.email,
						isAvailable: data.isAvailable,
						isValid: data.isValid,
						mobileNumber: data.formattedNumber
					}
				};
			} else {
				const errorMessage = isCustomApiError(error) ? error.errors?.[0]?.title : $t('global.error');

				return {
					error: {
						field: 'mobileNumber',
						/**
						 * @todo - Create a util to properly extract the errors
						 */
						message: errorMessage,
						type: 'api'
					}
				};
			}
		},
		[$t]
	);
};
